import { Divider, Pullout, PulloutType } from '@aon/aon-tws-common-ui';
import * as React from 'react';
import ParseHtml from 'html-react-parser';
import ContentConstants from '../../contentConstants';
import { DistributionType } from '../../models';
import { TFetchableEntity } from '../../models/IFetchableEntity';
import { IHierarchyGroupAccess } from '../../models/IHierarchyGroupAccess';
import { IReportingProfileConfig } from '../../models/IReportingProfileConfig';
import { IReportingProfileState } from '../../store/ReportingProfileStore';
import { Col3Col9, Section } from '../common';
import { ExpandableRowTable } from '../common/ExpandableTableRow';
import { ReportingProfileConfigEditModal } from './ReportingProfileConfigEditModal';
import { ReportingProfileConfigRow } from './ReportingProfileConfigRow';

export type ReportingProfileProps = IReportingProfileState & {
	distributionType: DistributionType;
	hierarchies: TFetchableEntity<IHierarchyGroupAccess[]>;
	hierarchyGroupAccessSaving: boolean;
	surveyId: number;
	CloseEditConfigModal: () => void;
	InitializeState: (surveyId: number, distributionType: DistributionType) => void;
	OpenEditConfigModal: (config: IReportingProfileConfig) => void;
	SaveConfig: (surveyId: number, config: IReportingProfileConfig, closeModal: boolean) => void;
	UpdateOutOfSyncReportingProfileConfigUserCount: (surveyId: number, reportingProfileConfigId: number, userCount: number) => void;
};

export class ReportingProfile extends React.PureComponent<ReportingProfileProps> {
	private get Warnings() {
		return {
			...(() => {
				const invalidProfiles = this.props.reportingProfileConfigs.filter((c) => c.isOpenImmediately === false && c.openOn == null);

				if (invalidProfiles.length > 0) {
					return {
						'Invalid Profiles': ContentConstants.REPORTING_PROFILES_MESSAGE_INVALID_GO_LIVE_DATE.replace(
							'{InvalidProfileNames}',
							invalidProfiles.map((p) => p.name).join(', '),
						),
					};
				}
			})(),

			...(this.GroupAccessInfoNotAvailable && {
				'Group Access Info N/A': ContentConstants.EMPLOYEEDATASOURCE_NOTAVAIL_WARNING,
			}),
		};
	}

	private get GroupAccessInfoNotAvailable() {
		const { fetchingGroupAccessOwners, hierarchies } = this.props;

		return fetchingGroupAccessOwners === 'N/A' || (!hierarchies.isLoading && hierarchies.couldNotFetchData);
	}

	public componentDidMount() {
		this.props.InitializeState(this.props.surveyId, this.props.distributionType);
	}

	public componentDidUpdate(previousProps: ReportingProfileProps) {
		// changes to the hierarchy group access will affect this component down stream
		// refreshing the data anytime hierarchy group access records are saved.
		if (!this.props.hierarchyGroupAccessSaving && previousProps.hierarchyGroupAccessSaving) {
			this.props.InitializeState(this.props.surveyId, this.props.distributionType);
		}

		// for new out of sync config id that has been added, update stored value via the api
		this.props.outOfSyncReportingProfileConfigIds.forEach((id) => {
			if (previousProps.outOfSyncReportingProfileConfigIds.indexOf(id) === -1) {
				const config = this.props.reportingProfileConfigs.find((c) => c.reportingProfileConfigId === id)!;

				this.props.UpdateOutOfSyncReportingProfileConfigUserCount(this.props.surveyId, id, config.userCount);
			}
		});
	}

	public render() {
		if (!this.props.showReportingProfile) {
			return null;
		}

		const { hrisId } = this.props;
		const hierarchies = new Map(this.props.hierarchies instanceof Array ? this.props.hierarchies.map((h) => [h.hierarchyId, h]) : []);

		return (
			<>
				<Section id={ContentConstants.ID_REPORTING_PROFILES} sectionHeader={ContentConstants.SECTIONHEADERS_REPORTING_PROFILES}>
					<Col3Col9 label={ContentConstants.SUBSECTIONHEADERS_INSTRUCTIONS}>
						{ParseHtml(ContentConstants.INSTRUCTIONALTEXT_REPORTING_PROFILE)}
					</Col3Col9>
					<Divider type='dashed' />
					<Col3Col9 className={'tws-reporting-profiles-table-part-label'} label={ContentConstants.REPORTING_PROFILES_LABEL}>
						{ParseHtml(ContentConstants.REPORTING_PROFILES_DESCRIPTION)}
					</Col3Col9>
					{Object.entries(this.Warnings).map(([key, warning]) => (
						<div key={key}>
							<Pullout type={PulloutType.Warning}>{ParseHtml(warning)}</Pullout>
							<br />
						</div>
					))}
					{this.props.reportingProfileConfigs.length > 0 && hrisId && !this.GroupAccessInfoNotAvailable && (
						<>
							<br />
							<ExpandableRowTable className={'tws-hierarchy-reporting-access-config-table'} lastalign={'right'}>
								<thead>
									<tr>
										<th />
										<th>{ContentConstants.REPORTING_PROFILES_TABLE_HEADER_PROFILE_NAME}</th>
										<th>{ContentConstants.REPORTING_PROFILES_TABLE_HEADER_REPORTING_USERS_COUNT}</th>
										<th>{ContentConstants.REPORTING_PROFILES_TABLE_HEADER_GO_LIVE_DATE}</th>
										<th>{ContentConstants.REPORTING_PROFILES_TABLE_HEADER_FILTERS_SEGMENTS}</th>
										<th>{ContentConstants.REPORTING_PROFILES_TABLE_HEADER_DEFAULT_HIERARCHY}</th>
										<th>{ContentConstants.REPORTING_PROFILES_TABLE_HEADER_ACTIONS}</th>
									</tr>
								</thead>
								<tbody>
									{this.props.reportingProfileConfigs.map((c) => {
										return (
											<ReportingProfileConfigRow
												key={c.name}
												config={c}
												defaultHierarchyName={c.defaultHierarchyId && hierarchies.get(c.defaultHierarchyId)?.name}
												hrisId={hrisId}
												editAction={this.props.OpenEditConfigModal}
											/>
										);
									})}
								</tbody>
							</ExpandableRowTable>
						</>
					)}
					{this.props.editConfig && (
						<ReportingProfileConfigEditModal
							initialModel={this.props.editConfig}
							saving={this.props.saving}
							isValid={true}
							onClosed={this.props.CloseEditConfigModal}
							onUpdate={(config, closeModal) => this.props.SaveConfig(this.props.surveyId, config, closeModal)}
							validationResults={this.props.validationResults}
						/>
					)}
				</Section>
			</>
		);
	}
}
